import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Box, Upload, Info, ExternalLink, Loader2, CheckCircle2, AlertCircle, Eye } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/use-toast';

interface ARFormProps {
  contentData: Record<string, string>;
  onUpdate: (data: Record<string, string>) => void;
}

export function ARForm({ contentData, onUpdate }: ARFormProps) {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Load model-viewer script if not present
    if (!document.getElementById('model-viewer-script')) {
      const script = document.createElement('script');
      script.id = 'model-viewer-script';
      script.type = 'module';
      script.src = 'https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js';
      document.head.appendChild(script);
    }
  }, []);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Validate file type
    if (!file.name.endsWith('.glb') && !file.name.endsWith('.gltf')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a .glb or .gltf file.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (20MB)
    if (file.size > 20 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 20MB.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      const fileName = `${user.id}/${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('ar-models')
        .upload(fileName, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('ar-models')
        .getPublicUrl(data.path);

      onUpdate({ ...contentData, url: publicUrl });
      toast({
        title: "Success",
        description: "3D model uploaded successfully!",
      });
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Could not upload file.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-2">
        <Box className="h-5 w-5 text-primary" />
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          AR Experience (3D Model)
        </h3>
      </div>

      <Card className="p-4 space-y-4">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Upload a .glb or .gltf file to create an AR experience. Users can view it in their space using their smartphone camera.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>3D Model URL (.glb)</Label>
            <Input 
              placeholder="https://example.com/model.glb" 
              value={contentData.url || ''} 
              onChange={e => onUpdate({ ...contentData, url: e.target.value })}
            />
          </div>

          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileUpload} 
            accept=".glb,.gltf" 
            className="hidden" 
          />

          <div 
            onClick={() => fileInputRef.current?.click()}
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer gap-4 ${
              isUploading ? 'bg-muted/50 border-muted-foreground/20' : 'bg-muted/30 border-muted-foreground/20 hover:border-primary/50 hover:bg-primary/5'
            }`}
          >
            <div className={`p-3 rounded-full ${isUploading ? 'bg-muted' : 'bg-primary/10'}`}>
              {isUploading ? (
                <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
              ) : contentData.url ? (
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              ) : (
                <Upload className="h-6 w-6 text-primary" />
              )}
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">
                {isUploading ? 'Uploading model...' : contentData.url ? 'Click to change model' : 'Click to upload 3D model'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Maximum size: 20MB (.glb only)</p>
            </div>
            {!isUploading && (
              <Button size="sm" variant="outline" type="button">
                {contentData.url ? 'Replace File' : 'Select File'}
              </Button>
            )}
          </div>

          {contentData.url && (
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-primary" />
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">3D Interactive Preview</h4>
                </div>
                <a 
                  href={contentData.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[10px] text-primary flex items-center gap-1 hover:underline"
                >
                  <ExternalLink className="h-3 w-3" /> View Source
                </a>
              </div>
              <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative border border-slate-800">
                {/* @ts-ignore - model-viewer is a custom element */}
                <model-viewer
                  src={contentData.url}
                  ar
                  ar-modes="webxr scene-viewer quick-look"
                  camera-controls
                  poster="poster.webp"
                  shadow-intensity="1"
                  auto-rotate
                  style={{ width: '100%', height: '100%', backgroundColor: '#0f172a' }}
                >
                  <div slot="poster" className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary/20" />
                  </div>
                </model-viewer>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                <Info className="h-3 w-3" />
                Drag to rotate, scroll to zoom. This is exactly what users will see.
              </p>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
}
